// substance/useSubstancePersistence.ts
//
// Task B3 — diff-based persistence layer for the real /ingredients page.
//
// The SlSubstanceDrawer is presentational: it emits the *full* next SubstanceRow
// via onUpdate(id, next) / onCreate(next) / onDelete(id) and keeps no idea of the
// server. This hook diffs each emitted SubstanceRow against a per-id snapshot of
// the last-persisted state and routes the deltas to the real inventory mutations
// (mirroring InventoryItemPanel's optimistic-update/rollback patterns):
//
//   scalars (lot/supplier/catalog/size/price/form/storage/expires/inStock)
//                              -> debounced patchInventory via useAutosave
//   name / code               -> patchLibraryItem
//   cas/ec/fema/internal       -> add/removeInventoryIdentifier (per value delta)
//   family (single-select)     -> remove old + add new flavor-family rel (replace)
//   stock.standard (single)    -> remove old + add new standard rel (replace)
//   stock.claims (multi)       -> add/removeInventoryClaim (per value delta)
//   stock.currency             -> price_currency_id/_raw on the autosave patch
//   tags                       -> add/removeInventoryTag (per value delta)
//   stock.loc (location)       -> setInventoryLocation
//
// Create: build CreateInventoryInput from the drawer's SubstanceRow (carrying the
// user-picked family, NOT the blank "fruity" default) -> createInventory.
// Delete: archiveInventory.
//
// The drawer's display-string model (family slug, location label, form/supplier
// names) is resolved against the live catalogs where possible; values that don't
// resolve to a catalog id are sent as *_raw where the endpoint supports it, or
// skipped otherwise (mirrors InventoryItemPanel's "typed value didn't match —
// reflect locally, don't PATCH" supplier behaviour). See the report for the
// exact live-verification list.
//
// The DEV/mock route does NOT use this hook — it passes no persist props, so the
// drawer stays local-state-only with zero network calls.

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { useAutosave } from "../useAutosave";
import { useInventoryCatalog } from "@/hooks/useInventoryCatalog";
import { useInventorySuppliers } from "@/hooks/useInventorySupplier";
import {
  addInventoryClaim,
  addInventoryFlavorFamily,
  addInventoryIdentifier,
  addInventoryStandard,
  addInventoryTag,
  archiveInventory,
  createCatalogFlavorFamily,
  createCatalogLocation,
  createInventory,
  patchInventory,
  patchLibraryItem,
  removeInventoryClaim,
  removeInventoryFlavorFamily,
  removeInventoryIdentifier,
  removeInventoryStandard,
  removeInventoryTagLink,
  setInventoryLocation,
} from "@/services/inventory_api";
import type {
  CreateInventoryInput,
  IdentifierKind,
  IdOrRaw,
  InventoryPatch,
  InventoryRow,
  LibraryItem,
  LibraryItemListResponse,
} from "@/services/inventory_types";
import type { TypeValue } from "../SidebarPanel/TypeSelector";

import type { SubstanceRow, SubstanceStock } from "./_model";

// ── infinite-query cache helpers (copied from InventoryItemPanel) ────────────
type LibraryItemsCache = {
  pages: LibraryItemListResponse[];
  pageParams: unknown[];
};

function patchInventoryInCache(
  qc: QueryClient,
  invId: string,
  patch: (inv: InventoryRow) => InventoryRow,
) {
  qc.setQueriesData<LibraryItemsCache>(
    { queryKey: ["inventory", "library-items"] },
    (data) => {
      if (!data) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((li) => ({
            ...li,
            inventory: li.inventory.map((inv) =>
              inv.id === invId ? patch(inv) : inv,
            ),
          })),
        })),
      };
    },
  );
}

function snapshotLibraryItemsCache(qc: QueryClient) {
  return qc.getQueriesData<LibraryItemsCache>({
    queryKey: ["inventory", "library-items"],
  });
}

function restoreLibraryItemsCache(
  qc: QueryClient,
  snapshot: ReturnType<typeof snapshotLibraryItemsCache>,
) {
  for (const [key, data] of snapshot) qc.setQueryData(key, data);
}

const invalidate = (qc: QueryClient) =>
  void qc.invalidateQueries({ queryKey: ["inventory", "library-items"] });

// ── identifier-kind mapping (model uppercase -> server lowercase) ────────────
const KIND_TO_SERVER: Record<string, IdentifierKind> = {
  cas: "cas",
  ec: "ec",
  fema: "fema",
  internal: "internal",
};

type IdField = "cas" | "ec" | "fema" | "internal";
const ID_FIELDS: IdField[] = ["cas", "ec", "fema", "internal"];

/** Stable identity key for a drawer claim (id pick or typed raw) so the
 *  before/after diff can detect adds/removes regardless of object identity. */
const claimKey = (c: TypeValue): string => (c.id ? `id:${c.id}` : `raw:${c.raw ?? ""}`);

/** Parse the drawer's combined "500 mL" size string into value + unit code. */
function parseSize(size: string): { value: number | null; unit: string } {
  const m = String(size ?? "").trim().match(/^([\d.]+)\s*(.*)$/);
  if (!m) return { value: null, unit: "" };
  const value = parseFloat(m[1]!);
  return { value: Number.isFinite(value) ? value : null, unit: (m[2] ?? "").trim() };
}

/** Convert a YYYY-MM-DD date to the ISO datetime the server's zod validator wants. */
function dateToIso(s: string): string | null {
  if (!s) return null;
  if (/T\d{2}:\d{2}/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00.000Z`;
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
}

export interface UseSubstancePersistenceResult {
  onPersistUpdate: (id: string, next: SubstanceRow) => void;
  /** Resolves to the created inventory id (so the caller can re-key the drawer
   *  to edit mode against the real row), or null on failure. */
  onPersistCreate: (next: SubstanceRow) => Promise<string | null>;
  onPersistDelete: (id: string) => void;
}

/**
 * @param liveItems  The flattened LibraryItem[] (used to map an inventory id ->
 *                   its parent library_item id for code/name patches).
 */
export function useSubstancePersistence(
  liveItems: LibraryItem[],
): UseSubstancePersistenceResult {
  const qc = useQueryClient();

  // ── live catalogs for display-string -> catalog-id resolution ──────────────
  const familyQ = useInventoryCatalog("flavor-family");
  const locationQ = useInventoryCatalog("location");
  const customQ = useInventoryCatalog("custom");
  const unitQ = useInventoryCatalog("unit");
  const formQ = useInventoryCatalog("form");
  const storageQ = useInventoryCatalog("storage-condition");
  const currencyQ = useInventoryCatalog("currency");
  const standardQ = useInventoryCatalog("standard");
  const claimQ = useInventoryCatalog("claim");
  const suppliersQ = useInventorySuppliers();

  // inventory id -> library_item id (for code/name patches).
  const liByInvId = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const li of liveItems) for (const inv of li.inventory) m.set(inv.id, li.id);
    return m;
  }, [liveItems]);

  // inventory id -> live InventoryRow (to read rel ids for removals).
  const invById = React.useMemo(() => {
    const m = new Map<string, InventoryRow>();
    for (const li of liveItems) for (const inv of li.inventory) m.set(inv.id, inv);
    return m;
  }, [liveItems]);

  // ── per-id snapshot of last-persisted SubstanceRow ─────────────────────────
  // Seeded lazily on the first diff per id (invToSubstanceSnapshot) so the first
  // emitted edit diffs against the live server row, not the empty map.
  const snapshotRef = React.useRef<Map<string, SubstanceRow>>(new Map());

  // ── autosave (scoped to the inventory id currently being edited) ───────────
  // `patchTargetIdRef` is the id the *pending* patch belongs to. It only moves
  // when there is no unflushed patch for a different row (we flush first on a
  // row switch — see onPersistUpdate). The save fn reads patchTargetIdRef at
  // flush time, so a flush kicked off before the switch still targets the old
  // row even though activeIdRef (used for diffing) has already advanced.
  const activeIdRef = React.useRef<string | null>(null);
  const patchTargetIdRef = React.useRef<string | null>(null);
  const { notifyChange, flush } = useAutosave<InventoryPatch>(async (patch) => {
    const id = patchTargetIdRef.current;
    if (!id) return;
    await patchInventory(id, patch);
    void qc.invalidateQueries({ queryKey: ["inventory", "library-items"] });
    void qc.invalidateQueries({ queryKey: ["inventory", "catalogs"] });
  });

  // Refs so the (stable) callbacks read the freshest catalogs.
  const catRef = React.useRef({
    familyQ,
    locationQ,
    customQ,
    unitQ,
    formQ,
    storageQ,
    currencyQ,
    standardQ,
    claimQ,
    suppliersQ,
  });
  catRef.current = {
    familyQ,
    locationQ,
    customQ,
    unitQ,
    formQ,
    storageQ,
    currencyQ,
    standardQ,
    claimQ,
    suppliersQ,
  };

  // ── resolvers (display string -> catalog id; fall back to raw) ─────────────
  const resolveFamilyId = React.useCallback((slug: string): string | null => {
    if (!slug) return null;
    const items = catRef.current.familyQ.data?.items ?? [];
    // Adapter derived family slug = displayText.toLowerCase(); invert it.
    const hit = items.find((e) => (e.displayText ?? "").toLowerCase() === slug);
    return hit?.id ?? null;
  }, []);

  const resolveLocationId = React.useCallback((label: string): string | null => {
    if (!label) return null;
    const items = catRef.current.locationQ.data?.items ?? [];
    const hit = items.find((e) => (e.displayText ?? "") === label);
    return hit?.id ?? null;
  }, []);

  const resolveCustomLink = React.useCallback(
    (inv: InventoryRow | undefined, displayText: string): string | null => {
      const link = (inv?.customs ?? []).find((c) => c.displayText === displayText);
      return link?.relId ?? null;
    },
    [],
  );

  // form / storage / unit / currency: prefer an id match, else send as raw.
  const idOrRaw = React.useCallback(
    (
      kind: "form" | "storage-condition" | "unit" | "currency",
      display: string,
    ): { id: string | null; raw: string | null } => {
      if (!display) return { id: null, raw: null };
      const q =
        kind === "form"
          ? catRef.current.formQ
          : kind === "storage-condition"
          ? catRef.current.storageQ
          : kind === "unit"
          ? catRef.current.unitQ
          : catRef.current.currencyQ;
      const items = q.data?.items ?? [];
      const hit = items.find(
        (e) =>
          e.id === display ||
          (e.slug ?? "") === display ||
          (e.displayText ?? "").toLowerCase() === display.toLowerCase() ||
          (e.code ?? "").toLowerCase() === display.toLowerCase(),
      );
      return hit ? { id: hit.id, raw: null } : { id: null, raw: display };
    },
    [],
  );

  const resolveSupplierId = React.useCallback((name: string): number | null => {
    if (!name) return null;
    const hit = (catRef.current.suppliersQ.data?.items ?? []).find(
      (s) => s.name === name,
    );
    return hit?.id ?? null;
  }, []);

  // standard / claim: prefer an id match (against the live catalog), else raw.
  // Returns an IdOrRaw the sub-resource POST bodies accept directly.
  const resolveStandard = React.useCallback((display: string): IdOrRaw | null => {
    if (!display) return null;
    const items = catRef.current.standardQ.data?.items ?? [];
    const hit = items.find(
      (e) =>
        e.id === display ||
        (e.slug ?? "") === display ||
        (e.displayText ?? "").toLowerCase() === display.toLowerCase() ||
        (e.code ?? "").toLowerCase() === display.toLowerCase(),
    );
    return hit ? { id: hit.id } : { raw: display };
  }, []);

  const resolveClaim = React.useCallback((value: TypeValue): IdOrRaw | null => {
    // The drawer stores each claim as { id } (catalog pick) or { raw } (typed).
    if (value.id) {
      const items = catRef.current.claimQ.data?.items ?? [];
      const hit = items.find(
        (e) =>
          e.id === value.id ||
          (e.slug ?? "") === value.id ||
          (e.displayText ?? "").toLowerCase() === value.id!.toLowerCase() ||
          (e.code ?? "").toLowerCase() === value.id!.toLowerCase(),
      );
      return hit ? { id: hit.id } : { raw: value.id };
    }
    if (value.raw) return { raw: value.raw };
    return null;
  }, []);

  // ── M:N mutation helpers (optimistic + rollback) ───────────────────────────
  const addIdentifier = React.useCallback(
    async (invId: string, kind: IdentifierKind, value: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        identifiers: [
          ...(inv.identifiers ?? []),
          { id: `optimistic-id-${Date.now()}`, kind, value },
        ],
      }));
      try {
        await addInventoryIdentifier(invId, { kind, value });
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
      }
    },
    [qc],
  );

  const removeIdentifier = React.useCallback(
    async (invId: string, identifierId: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        identifiers: (inv.identifiers ?? []).filter((i) => i.id !== identifierId),
      }));
      try {
        await removeInventoryIdentifier(invId, identifierId);
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
      }
    },
    [qc],
  );

  const addTag = React.useCallback(
    async (invId: string, displayText: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        customs: [
          ...(inv.customs ?? []),
          {
            relId: `optimistic-custom-${Date.now()}`,
            customId: `optimistic-custom-${Date.now()}`,
            displayText,
          },
        ],
      }));
      try {
        await addInventoryTag(invId, { display_text: displayText });
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
        void qc.invalidateQueries({ queryKey: ["inventory", "catalogs", "custom"] });
      }
    },
    [qc],
  );

  const removeTag = React.useCallback(
    async (invId: string, linkId: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        customs: (inv.customs ?? []).filter((c) => c.relId !== linkId),
      }));
      try {
        await removeInventoryTagLink(invId, linkId);
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
      }
    },
    [qc],
  );

  /** Single-select flavor family: remove every existing rel then add the new id. */
  const replaceFamily = React.useCallback(
    async (invId: string, inv: InventoryRow | undefined, familyId: string) => {
      const existing = inv?.families ?? [];
      if (existing.some((f) => f.familyId === familyId)) return; // already set
      const prev = snapshotLibraryItemsCache(qc);
      const fam = (catRef.current.familyQ.data?.items ?? []).find(
        (e) => e.id === familyId,
      );
      patchInventoryInCache(qc, invId, (i) => ({
        ...i,
        families: [
          {
            relId: `optimistic-fam-${Date.now()}`,
            familyId,
            displayText: fam?.displayText ?? familyId,
            color: fam?.color ?? null,
          },
        ],
      }));
      try {
        for (const f of existing) await removeInventoryFlavorFamily(invId, f.relId);
        await addInventoryFlavorFamily(invId, familyId);
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
      }
    },
    [qc],
  );

  /** Single-select standard: remove every existing rel then add the new one. */
  const replaceStandard = React.useCallback(
    async (invId: string, inv: InventoryRow | undefined, body: IdOrRaw, displayText: string) => {
      const existing = inv?.standards ?? [];
      // Already set to this exact id? Skip.
      if (
        "id" in body &&
        existing.length === 1 &&
        existing[0]!.standardId === body.id
      ) {
        return;
      }
      const prev = snapshotLibraryItemsCache(qc);
      const isId = "id" in body;
      patchInventoryInCache(qc, invId, (i) => ({
        ...i,
        standards: [
          {
            relId: `optimistic-std-${Date.now()}`,
            standardId: isId ? body.id : null,
            standardRaw: isId ? null : body.raw,
            displayText,
          },
        ],
      }));
      try {
        for (const s of existing) await removeInventoryStandard(invId, s.relId);
        await addInventoryStandard(
          invId,
          isId ? { standard_id: body.id } : { standard_raw: body.raw },
        );
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
        void qc.invalidateQueries({ queryKey: ["inventory", "catalogs", "standard"] });
      }
    },
    [qc],
  );

  const addClaimRel = React.useCallback(
    async (invId: string, body: IdOrRaw, displayText: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      const isId = "id" in body;
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        claims: [
          ...(inv.claims ?? []),
          {
            relId: `optimistic-clm-${Date.now()}`,
            claimId: isId ? body.id : null,
            claimRaw: isId ? null : body.raw,
            displayText,
          },
        ],
      }));
      try {
        await addInventoryClaim(
          invId,
          isId ? { claim_id: body.id } : { claim_raw: body.raw },
        );
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
        void qc.invalidateQueries({ queryKey: ["inventory", "catalogs", "claim"] });
      }
    },
    [qc],
  );

  const removeClaimRel = React.useCallback(
    async (invId: string, relId: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        claims: (inv.claims ?? []).filter((c) => c.relId !== relId),
      }));
      try {
        await removeInventoryClaim(invId, relId);
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
      }
    },
    [qc],
  );

  const setLocation = React.useCallback(
    async (invId: string, locationId: string, displayText: string) => {
      const prev = snapshotLibraryItemsCache(qc);
      patchInventoryInCache(qc, invId, (inv) => ({
        ...inv,
        location: {
          relId: `optimistic-loc-${Date.now()}`,
          locationId,
          displayText,
        },
      }));
      try {
        await setInventoryLocation(invId, { location_id: locationId });
      } catch {
        restoreLibraryItemsCache(qc, prev);
      } finally {
        invalidate(qc);
      }
    },
    [qc],
  );

  // ── onPersistUpdate: diff next vs snapshot ─────────────────────────────────
  const onPersistUpdate = React.useCallback(
    (id: string, next: SubstanceRow) => {
      // Switching to a different row: flush any pending patch for the previous
      // row first so its debounced patch can't be misrouted to this id.
      if (activeIdRef.current !== null && activeIdRef.current !== id) {
        void flush();
      }
      activeIdRef.current = id;
      const prev = snapshotRef.current.get(id) ?? invToSubstanceSnapshot(invById.get(id), next);

      // 1) Library-item scalars: name + code.
      const liId = liByInvId.get(id);
      if (liId) {
        const liPatch: { code?: string; name?: string } = {};
        if (next.code !== prev.code) liPatch.code = next.code;
        if (next.name !== prev.name) liPatch.name = next.name;
        if (Object.keys(liPatch).length > 0) {
          void patchLibraryItem(liId, liPatch).then(() => invalidate(qc));
        }
      }

      // 2) Inventory scalars (debounced autosave). Point the pending-patch
      // target at this row (the row-switch flush above already drained any
      // patch still owed to the previous row).
      patchTargetIdRef.current = id;
      const patch: InventoryPatch = {};
      const a = next.stock ?? EMPTY_STOCK;
      const b = prev.stock ?? EMPTY_STOCK;
      if (a.lot !== b.lot) patch.lot_no = a.lot || null;
      if (a.catalog !== b.catalog) patch.supplier_catalog_no = a.catalog || null;
      if (a.inStock !== b.inStock) patch.in_stock = a.inStock;
      if (a.supplier !== b.supplier) {
        const sid = resolveSupplierId(a.supplier);
        if (sid != null) patch.supplier_id = sid;
        // else: typed value didn't match a known supplier — reflect in UI only.
      }
      if (a.size !== b.size) {
        const { value, unit } = parseSize(a.size);
        if (value != null) patch.size_value = value;
        const u = idOrRaw("unit", unit);
        patch.size_unit_id = u.id;
        patch.size_unit_raw = u.raw;
      }
      if (a.price !== b.price) patch.price_value = a.price;
      // Currency is a DrawerStock extra ({ id } | { raw }) not on SubstanceStock.
      const aCurrency = (a as Partial<{ currency: TypeValue }>).currency;
      const bCurrency = (b as Partial<{ currency: TypeValue }>).currency;
      if (
        (aCurrency?.id ?? null) !== (bCurrency?.id ?? null) ||
        (aCurrency?.raw ?? null) !== (bCurrency?.raw ?? null)
      ) {
        if (aCurrency?.id) {
          // Resolve against the live currency catalog (id / code / display).
          const c = idOrRaw("currency", aCurrency.id);
          patch.price_currency_id = c.id;
          patch.price_currency_raw = c.raw;
        } else if (aCurrency?.raw) {
          patch.price_currency_id = null;
          patch.price_currency_raw = aCurrency.raw;
        } else {
          patch.price_currency_id = null;
          patch.price_currency_raw = null;
        }
      }
      if (a.form !== b.form) {
        const f = idOrRaw("form", a.form);
        patch.form_id = f.id;
        patch.form_raw = f.raw;
      }
      if (a.expires !== b.expires) patch.expires_at = dateToIso(a.expires);
      // Drawer's stock.storage (storage condition) is a DrawerStock extra not on
      // SubstanceRow — read defensively if present.
      const aStorage = (a as Partial<{ storage: string }>).storage ?? "";
      const bStorage = (b as Partial<{ storage: string }>).storage ?? "";
      if (aStorage !== bStorage) {
        const sc = idOrRaw("storage-condition", aStorage);
        patch.storage_condition_id = sc.id;
        patch.storage_condition_raw = sc.raw;
      }
      // Remarks is a DrawerStock-adjacent extra on the substance object.
      const aRemarks = (next as Partial<{ remarks: string }>).remarks ?? "";
      const bRemarks = (prev as Partial<{ remarks: string }>).remarks ?? "";
      if (aRemarks !== bRemarks) patch.remarks = aRemarks || null;
      // Substance type (compound↔mixture). Flip the NDB table AND clear the now-
      // stale substance_rowid so the (table, rowid) pair can never mismatch.
      if (next.type !== prev.type) {
        patch.substance_ndb_table =
          next.type === "compound" ? "public.compound" : "public.food";
        patch.substance_rowid = null;
      }
      if (Object.keys(patch).length > 0) notifyChange(patch);

      // 3) Identifiers (per-value delta across cas/ec/fema/internal).
      const liveInv = invById.get(id);
      for (const field of ID_FIELDS) {
        const before = new Set(prev[field] ?? []);
        const after = new Set(next[field] ?? []);
        const kind = KIND_TO_SERVER[field]!;
        for (const v of after) if (!before.has(v)) void addIdentifier(id, kind, v);
        for (const v of before) {
          if (!after.has(v)) {
            const ident = (liveInv?.identifiers ?? []).find(
              (i) => i.kind === kind && i.value === v,
            );
            if (ident?.id) void removeIdentifier(id, ident.id);
          }
        }
      }

      // 3b) Extra identifiers (pubchem/chemspider/inchikey/…) — per-value delta.
      // extraIds.kind is the server-lowercase form (both the adapter and the
      // drawer's add-handler store it lowercase) so it maps straight to
      // addInventoryIdentifier and the snapshot diff keys match exactly.
      const extraKey = (e: { kind: string; value: string }) => `${e.kind} ${e.value}`;
      const beforeExtra = new Map((prev.extraIds ?? []).map((e) => [extraKey(e), e] as const));
      const afterExtra = new Map((next.extraIds ?? []).map((e) => [extraKey(e), e] as const));
      for (const [k, e] of afterExtra) {
        if (!beforeExtra.has(k)) void addIdentifier(id, e.kind as IdentifierKind, e.value);
      }
      for (const [k, e] of beforeExtra) {
        if (!afterExtra.has(k)) {
          const ident = (liveInv?.identifiers ?? []).find(
            (i) => i.kind === e.kind && i.value === e.value,
          );
          if (ident?.id) void removeIdentifier(id, ident.id);
        }
      }

      // 4) Flavor family (single-select -> set/replace). A typed family that
      // isn't a catalog row yet is CREATED (server find-or-create, carrying the
      // drawer-picked color/label) then attached.
      if (next.family !== prev.family && next.family) {
        const famId = resolveFamilyId(next.family);
        if (famId) {
          void replaceFamily(id, liveInv, famId);
        } else {
          void createCatalogFlavorFamily({
            display_text: next.familyLabel ?? next.family,
            ...(next.familyColor ? { color: next.familyColor } : {}),
          })
            .then((created) => {
              void qc.invalidateQueries({ queryKey: ["inventory", "catalogs", "flavor-family"] });
              return replaceFamily(id, liveInv, created.id);
            })
            .catch(() => invalidate(qc));
        }
      }

      // 4b) Standard (single-select -> set/replace). DrawerStock extra.
      const aStandard = (a as Partial<{ standard: string }>).standard ?? "";
      const bStandard = (b as Partial<{ standard: string }>).standard ?? "";
      if (aStandard !== bStandard && aStandard) {
        const body = resolveStandard(aStandard);
        if (body) void replaceStandard(id, liveInv, body, aStandard);
      }

      // 4c) Claims (multi-select -> per-value delta). DrawerStock extra.
      const aClaims = (a as Partial<{ claims: TypeValue[] }>).claims ?? [];
      const bClaims = (b as Partial<{ claims: TypeValue[] }>).claims ?? [];
      const beforeClaims = new Map(bClaims.map((c) => [claimKey(c), c]));
      const afterClaims = new Map(aClaims.map((c) => [claimKey(c), c]));
      for (const [key, c] of afterClaims) {
        if (!beforeClaims.has(key)) {
          const body = resolveClaim(c);
          if (body) void addClaimRel(id, body, c.id ?? c.raw ?? "");
        }
      }
      for (const [key, c] of beforeClaims) {
        if (!afterClaims.has(key)) {
          // Find the live rel to delete: match by claimId (id pick) or raw.
          const rel = (liveInv?.claims ?? []).find((r) =>
            c.id
              ? r.claimId === c.id || r.displayText === c.id
              : r.claimRaw === c.raw || r.displayText === c.raw,
          );
          if (rel?.relId) void removeClaimRel(id, rel.relId);
        }
      }

      // 5) Custom tags (per-value delta).
      const beforeTags = new Set(prev.tags ?? []);
      const afterTags = new Set(next.tags ?? []);
      for (const t of afterTags) if (!beforeTags.has(t)) void addTag(id, t);
      for (const t of beforeTags) {
        if (!afterTags.has(t)) {
          const linkId = resolveCustomLink(liveInv, t);
          if (linkId) void removeTag(id, linkId);
        }
      }

      // 6) Location. A typed value that isn't a catalog row yet is CREATED in the
      // caller's scope (server find-or-create) then attached.
      if (a.loc !== b.loc && a.loc) {
        const locId = resolveLocationId(a.loc);
        if (locId) {
          void setLocation(id, locId, a.loc);
        } else {
          const storageId = idOrRaw("storage-condition", aStorage).id ?? undefined;
          void createCatalogLocation({ display_text: a.loc, storage_condition_id: storageId })
            .then((created) => {
              void qc.invalidateQueries({ queryKey: ["inventory", "catalogs", "location"] });
              return setLocation(id, created.id, a.loc);
            })
            .catch(() => invalidate(qc));
        }
      }

      // Update snapshot to the just-emitted row.
      snapshotRef.current.set(id, next);
    },
    [
      qc,
      liByInvId,
      invById,
      notifyChange,
      flush,
      resolveSupplierId,
      idOrRaw,
      addIdentifier,
      removeIdentifier,
      resolveFamilyId,
      replaceFamily,
      resolveStandard,
      replaceStandard,
      resolveClaim,
      addClaimRel,
      removeClaimRel,
      addTag,
      removeTag,
      resolveCustomLink,
      resolveLocationId,
      setLocation,
    ],
  );

  // ── onPersistCreate ────────────────────────────────────────────────────────
  const onPersistCreate = React.useCallback(
    async (next: SubstanceRow): Promise<string | null> => {
      const stock = next.stock ?? EMPTY_STOCK;
      const identifiers: Array<{ kind: IdentifierKind; value: string }> = [];
      for (const field of ID_FIELDS) {
        const kind = KIND_TO_SERVER[field]!;
        for (const value of next[field] ?? []) identifiers.push({ kind, value });
      }
      for (const e of next.extraIds ?? []) {
        identifiers.push({ kind: e.kind as IdentifierKind, value: e.value });
      }

      const { value: sizeValue, unit } = parseSize(stock.size);
      const sizeUnit = idOrRaw("unit", unit);
      const form = idOrRaw("form", stock.form);
      // Carry the user-picked family (NOT the blank "fruity" default): only
      // include it when it resolves to a real catalog id.
      const familyId = resolveFamilyId(next.family);
      const supplierId = resolveSupplierId(stock.supplier);
      const locationId = resolveLocationId(stock.loc);
      const remarks = (next as Partial<{ remarks: string }>).remarks ?? "";

      // Standards (single) / claims (multi) / currency — DrawerStock extras.
      const standardDisplay = (stock as Partial<{ standard: string }>).standard ?? "";
      const standardBody = standardDisplay ? resolveStandard(standardDisplay) : null;
      const standards: IdOrRaw[] = standardBody ? [standardBody] : [];
      const claims: IdOrRaw[] = ((stock as Partial<{ claims: TypeValue[] }>).claims ?? [])
        .map((c) => resolveClaim(c))
        .filter((b): b is IdOrRaw => b !== null);
      const currencyVal = (stock as Partial<{ currency: TypeValue }>).currency;
      const currency = currencyVal?.id
        ? idOrRaw("currency", currencyVal.id)
        : currencyVal?.raw
        ? { id: null, raw: currencyVal.raw }
        : { id: null, raw: null };

      const body: CreateInventoryInput = {
        label_name: next.name,
        identifiers,
        ...(next.type === "compound" || next.type === "mixture"
          ? {
              substance_ndb_table:
                next.type === "compound" ? "public.compound" : "public.food",
            }
          : {}),
        ...(supplierId != null ? { supplier_id: supplierId } : {}),
        ...(stock.catalog ? { supplier_catalog_no: stock.catalog } : {}),
        ...(stock.lot ? { lot_no: stock.lot } : {}),
        ...(sizeValue != null ? { size_value: sizeValue } : {}),
        ...(sizeUnit.id ? { size_unit_id: sizeUnit.id } : {}),
        ...(sizeUnit.raw ? { size_unit_raw: sizeUnit.raw } : {}),
        ...(stock.price ? { price_value: stock.price } : {}),
        ...(currency.id ? { price_currency_id: currency.id } : {}),
        ...(currency.raw ? { price_currency_raw: currency.raw } : {}),
        ...(form.id ? { form_id: form.id } : {}),
        ...(form.raw ? { form_raw: form.raw } : {}),
        ...(familyId ? { family_ids: [familyId] } : {}),
        ...(standards.length ? { standards } : {}),
        ...(claims.length ? { claims } : {}),
        ...(locationId ? { location_id: locationId } : {}),
        ...(stock.expires ? { expires_at: dateToIso(stock.expires) ?? undefined } : {}),
        ...(remarks ? { remarks } : {}),
        ...(next.code ? { code: next.code } : {}),
      };

      try {
        const res = await createInventory(body);
        await qc.invalidateQueries({ queryKey: ["inventory", "library-items"] });
        return res.inventory_id;
      } catch {
        return null;
      }
    },
    [
      qc,
      idOrRaw,
      resolveFamilyId,
      resolveSupplierId,
      resolveLocationId,
      resolveStandard,
      resolveClaim,
    ],
  );

  // ── onPersistDelete (archive) ──────────────────────────────────────────────
  const onPersistDelete = React.useCallback(
    (id: string) => {
      void archiveInventory(id)
        .then(() => invalidate(qc))
        .catch(() => {
          // Archive failed — invalidate anyway so the optimistically-removed
          // row re-appears from the server on refetch (no silent loss).
          invalidate(qc);
        });
    },
    [qc],
  );

  return { onPersistUpdate, onPersistCreate, onPersistDelete };
}

const EMPTY_STOCK: SubstanceStock = {
  inStock: true,
  supplier: "",
  catalog: "",
  lot: "",
  size: "",
  price: 0,
  form: "",
  loc: "",
  expires: "",
};

/** Build a baseline snapshot SubstanceRow from the live InventoryRow when the
 *  drawer first emits an edit for an id we haven't snapshotted yet. We derive
 *  only the diff-relevant fields; everything else falls back to `emitted` so the
 *  first diff sees zero spurious deltas for untouched fields. */
function invToSubstanceSnapshot(
  inv: InventoryRow | undefined,
  emitted: SubstanceRow,
): SubstanceRow {
  if (!inv) return emitted;
  const family = (inv.families ?? [])[0]?.displayText?.toLowerCase() ?? "neutral";
  return {
    ...emitted,
    code: emitted.code, // patched via library-item; emitted seed is authoritative
    name: emitted.name,
    family, // no collapse — matches fromLibraryItem.familySlugOf (custom families keep their slug)
    familyColor: (inv.families ?? [])[0]?.color ?? null,
    familyLabel: (inv.families ?? [])[0]?.displayText,
    // type mirrors fromLibraryItem.typeOf exactly (null NDB table → "mixture")
    // so the first diff sees no spurious type change.
    type: inv.substanceNdbTable === "public.compound" ? "compound" : "mixture",
    cas: (inv.identifiers ?? []).filter((i) => i.kind === "cas").map((i) => i.value),
    ec: (inv.identifiers ?? []).filter((i) => i.kind === "ec").map((i) => i.value),
    fema: (inv.identifiers ?? []).filter((i) => i.kind === "fema").map((i) => i.value),
    internal: (inv.identifiers ?? [])
      .filter((i) => i.kind === "internal")
      .map((i) => i.value),
    // Extra identifiers hydrated identically to fromLibraryItem (server-lowercase
    // kind) so the diff baseline matches the drawer state.
    extraIds: (inv.identifiers ?? [])
      .filter((i) => !["cas", "ec", "fema", "internal"].includes(i.kind))
      .map((i) => ({ kind: i.kind, value: i.value })),
    tags: (inv.customs ?? []).map((c) => c.displayText),
    stock: {
      inStock: inv.outOfStockAt == null && inv.archivedAt == null,
      supplier: inv.supplierName ?? "",
      catalog: inv.supplierCatalogNo ?? "",
      lot: inv.lotNo ?? "",
      size: [inv.sizeValue, inv.sizeUnitRaw ?? inv.sizeUnitLabel ?? ""]
        .filter(Boolean)
        .join(" ")
        .trim(),
      price: Number(inv.priceValue ?? 0),
      form: inv.formRaw ?? inv.formLabel ?? "",
      loc: inv.location?.displayText ?? "",
      expires: inv.expiresAt ? inv.expiresAt.slice(0, 10) : "",
      // Standard / claims / currency / storage are now hydrated onto the drawer
      // by the list adapter (fromLibraryItem.ts → stockOf) from this SAME live
      // InventoryRow. The baseline MUST be derived identically so the first
      // emitted edit diffs against the pre-populated drawer state and fires ZERO
      // spurious add/remove/patch on these facets. (Mirror stockOf exactly.)
      //
      //   standard  — single-select PRIMARY: standards[0].standardId ?? displayText
      //   claims    — { id: claimId } | { raw: displayText } per rel
      //   currency  — { id } | { raw } | undefined
      //   storage   — storageConditionId ?? storageConditionRaw ?? ""
      standard:
        (inv.standards ?? [])[0]?.standardId ??
        (inv.standards ?? [])[0]?.displayText ??
        undefined,
      claims: (inv.claims ?? []).map((c) =>
        c.claimId ? { id: c.claimId } : { raw: c.displayText },
      ),
      currency: inv.priceCurrencyId
        ? { id: inv.priceCurrencyId }
        : inv.priceCurrencyRaw
        ? { raw: inv.priceCurrencyRaw }
        : undefined,
      storage: inv.storageConditionId ?? inv.storageConditionRaw ?? undefined,
    } as SubstanceStock,
  };
}
